import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree, ActivatedRouteSnapshot } from '@angular/router';
import { UserProfileService } from '../modules/usermanagement/services/user-profile.service';
import { IDSAuthService } from './idsauth.service';

@Injectable({
  providedIn: 'root'
})
export class RoleAuthGuard implements CanActivate {

  // Define hierarchical navigation paths - these modules can be accessed through drill-down
  private hierarchicalModules = ['/clients', '/customers', '/locations', '/devices'];

  constructor(
    private idsAuthService: IDSAuthService,
    private router: Router,
    private userProfileService: UserProfileService
  ) {}

  canActivate(route: ActivatedRouteSnapshot): boolean | UrlTree {
    // First check if user is authenticated
    if (!this.idsAuthService.isAuthenticated()) {
      return this.router.createUrlTree(['/login']);
    }

    // Ensure profile is available — BehaviorSubject may be null if the service
    // was instantiated before localStorage was written (e.g. post-login race) or
    // if it was cleared by a previous logout flow.
    if (!this.userProfileService.getCurrentUserProfile()) {
      this.userProfileService.loadUserProfileFromStorage();
    }

    // Get the current route path - handle both parent and child routes
    let currentRoute = '/' + route.url.join('/');

    // If this is a lazy-loaded module, get the parent route path
    if (route.routeConfig && route.routeConfig.path) {
      // Build full path from parent route
      const parentPath = route.routeConfig.path;
      if (parentPath && !currentRoute.includes(parentPath)) {
        currentRoute = '/' + parentPath;
      }
    }

    // Check if user has direct access to this route
    if (this.userProfileService.hasRouteAccess(currentRoute)) {
      return true;
    }

    // Check if this is a hierarchical navigation (has query params indicating drill-down)
    if (this.isHierarchicalNavigation(route, currentRoute)) {
      console.log(`Allowing hierarchical navigation to ${currentRoute} with params:`, route.queryParams);
      return true;
    }

    // User doesn't have access - redirect to access-denied
    const userProfile = this.userProfileService.getCurrentUserProfile();
    const userRole = userProfile?.roleName || 'Unknown';
    console.warn(`User with role "${userRole}" does not have access to route: ${currentRoute}`);

    return this.router.createUrlTree(['/access-denied']);
  }

  /**
   * Check if this is a hierarchical drill-down navigation
   * Allows navigation to modules when coming from a parent entity
   */
  private isHierarchicalNavigation(route: ActivatedRouteSnapshot, currentRoute: string): boolean {
    // Only check hierarchical modules
    if (!this.hierarchicalModules.includes(currentRoute)) {
      return false;
    }

    const queryParams = route.queryParams;
    const routeParams = route.params;

    // Check query parameters and route parameters
    const hasClientIdParam = queryParams['clientId'] || queryParams['clientGuid'] || routeParams['clientId'];
    const hasCustomerIdParam = queryParams['customerId'] || queryParams['customerGuid'] || queryParams['iotCustomerId'] || routeParams['customerId'];
    const hasLocationIdParam = queryParams['locationId'] || queryParams['locationGuid'] || queryParams['iotLocationId'] || routeParams['locationId'];

    // Also check localStorage for hierarchical context (used by breadcrumb navigation)
    const hasClientIdStorage = localStorage.getItem('clientId') || localStorage.getItem('iotClientId');
    const hasCustomerIdStorage = localStorage.getItem('customerId') || localStorage.getItem('iotCustomerId');
    const hasLocationIdStorage = localStorage.getItem('locationId') || localStorage.getItem('iotLocationId');

    // Combine both query params and localStorage checks
    const hasClientId = hasClientIdParam || hasClientIdStorage;
    const hasCustomerId = hasCustomerIdParam || hasCustomerIdStorage;
    const hasLocationId = hasLocationIdParam || hasLocationIdStorage;

    // Allow access based on hierarchical context
    switch (currentRoute) {
      case '/customers':
        // Allow if coming from a client (via query param or localStorage)
        return !!hasClientId;
      case '/locations':
        // Allow if coming from a customer or client
        return !!(hasCustomerId || hasClientId);
      case '/devices':
        // Allow if coming from location, customer, or client
        return !!(hasLocationId || hasCustomerId || hasClientId);
      default:
        return false;
    }
  }
}